import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  SceneCategory,
  PromptTemplate,
  UserTemplate,
  GenerateParams,
  FilterOptions,
  ImageSize,
  ImageQuality,
  ImageFormat,
  BackgroundType,
  Project,
  Conversation,
  ChatMessage,
  MessageMetadata,
} from '@/types';
import { authApi, configApi, creditsApi, projectsApi, conversationsApi } from '@/api/client';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  createdAt?: string;
  isCoach?: boolean;
  hasLocalPassword?: boolean;
  userSource?: 'local' | 'wp';
  wpUserId?: number;
  identities?: string[];
  industryId?: number;
  industryRole?: string;
  city?: string;
  workYears?: number;
  bio?: string;
  isVip?: boolean;
  isPermanentVip?: boolean;
  vipEndTime?: string;
  // v1.2.0 新增字段
  credits?: number;
  totalRecharge?: number;
  inviteCode?: string;
  invitedBy?: number;
  isEarlyUser?: boolean;
  isCoachVerified?: boolean;
  coachVerifiedAt?: string;
  coachMonthlyRewardAt?: string;
}

export interface UserProfile {
  displayName?: string;
  avatarUrl?: string;
  company: string;
  industry: string;
  role: string;
  team: string;
  productStage: string;
  industryId?: number;
  industryRole?: string;
  city?: string;
  workYears?: number;
  bio?: string;
}

const defaultProfile: UserProfile = {
  displayName: '',
  avatarUrl: '',
  company: '',
  industry: '',
  role: '',
  team: '',
  productStage: '',
};

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function debouncedSave(get: () => AppState) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    const { userProfile, authToken } = get();
    if (!authToken) return;
    try {
      await configApi.updateUserConfig({ profile: userProfile });
    } catch {
      // will retry on next change
    }
  }, 800);
}

const SCENE_LABELS: Record<string, string> = {
  'market-research': '市场调研',
  'user-research': '用户研究',
  'competitive-analysis': '竞品分析',
  'demand-insight': '需求洞察',
  'product-strategy': '产品策略',
  'product-planning': '产品规划',
  'prototype-design': '原型设计',
  'prd': 'PRD 撰写',
  'review-report': '评审汇报',
  'launch-track': '上线发布',
  'data-review': '数据复盘',
  'growth-ops': '增长运营',
};

interface AppState {
  authToken: string | null;
  authUser: AuthUser | null;
  scenes: SceneCategory[];
  templates: PromptTemplate[];
  customTemplates: UserTemplate[];
  projects: Project[];
  sceneProjectMap: Record<string, { projectId: number; imageCount: number }>;
  conversations: Conversation[];
  currentConversationId: number | null;
  chatMessages: ChatMessage[];
  currentConversationSelections: {
    phaseId?: string;
    sceneId?: string;
    chartId?: string;
  };
  isGenerating: boolean;
  filters: FilterOptions;
  generateParams: GenerateParams;
  userProfile: UserProfile;
  userInitialized: boolean;
  configLoaded: boolean;
  creditBalance: number;

  setAuthToken: (token: string | null) => void;
  setAuthUser: (user: AuthUser | null) => void;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, displayName?: string, inviteCode?: string, verificationCode?: string) => Promise<void>;
  wpLogin: (wpToken: string, wpPassword: string, inviteCode?: string) => Promise<{ code?: string; wpUser?: any; existingUserId?: number; existingUsername?: string }>;
  wpBindEmail: (email: string, existingUserId: number, verificationCode: string, wpToken: string) => Promise<void>;
  wpSendBindCode: (wpToken: string) => Promise<void>;
  wpSetUsername: (wpToken: string, wpPassword: string, newUsername: string, inviteCode?: string) => Promise<void>;
  wpRegister: (wpToken: string, password: string, email?: string) => Promise<void>;
  setLocalPassword: (password: string, currentPassword?: string) => Promise<void>;
  logout: () => void;

  setScenes: (scenes: SceneCategory[]) => void;
  setTemplates: (templates: PromptTemplate[]) => void;
  addCustomTemplate: (template: UserTemplate) => void;
  removeCustomTemplate: (id: string) => void;

  loadSceneProjects: () => Promise<void>;
  ensureSceneProject: (sceneId: string) => Promise<number>;

  fetchConversations: () => Promise<void>;
  createConversation: (sceneId?: string, phaseId?: string, chartId?: string, title?: string) => Promise<number>;
  setCurrentConversation: (id: number | null) => void;
  setCurrentConversationSelections: (selections: { phaseId?: string; sceneId?: string; chartId?: string }) => void;
  loadConversation: (id: number) => Promise<void>;
  deleteConversation: (id: number) => Promise<void>;
  renameConversation: (id: number, title: string) => Promise<void>;
  updateConversationTitle: (id: number, title: string) => void;
  addChatMessage: (msg: ChatMessage) => void;
  updateMessageReaction: (msgId: number, type: 'like' | 'dislike' | 'clear') => Promise<void>;
  updateMessageMetadata: (msgId: number, metadata: Partial<MessageMetadata>) => void;
  initChat: () => Promise<void>;

  setIsGenerating: (isGenerating: boolean) => void;
  setFilters: (filters: Partial<FilterOptions>) => void;
  setGenerateParams: (params: Partial<GenerateParams>) => void;
  resetGenerateParams: () => void;
  setUserProfile: (profile: Partial<UserProfile>) => void;
  setUserInitialized: (initialized: boolean) => void;
  loadConfigFromServer: () => Promise<void>;
  saveConfigToServer: () => Promise<void>;
  fetchCreditBalance: () => Promise<void>;
  fetchAuthUser: () => Promise<void>;
}

const defaultParams: GenerateParams = {
  prompt: '',
  size: '1024x1024' as ImageSize,
  quality: 'high' as ImageQuality,
  format: 'png' as ImageFormat,
  background: 'auto' as BackgroundType,
  n: 1,
  textRendering: true,
  safetyLevel: 'auto',
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      authToken: null,
      authUser: null,
      scenes: [],
      templates: [],
      customTemplates: [],
      projects: [],
      sceneProjectMap: {},
      conversations: [],
      currentConversationId: null,
      chatMessages: [],
      currentConversationSelections: {},
      isGenerating: false,
      filters: { industries: [], devices: [], styles: [], sizes: [], search: '' },
      generateParams: defaultParams,
      userProfile: defaultProfile,
      userInitialized: false,
      configLoaded: false,
      creditBalance: 0,

      setAuthToken: (token) => set({ authToken: token }),
      setAuthUser: (user) => set({ authUser: user }),

      login: async (username, password) => {
        const res = await authApi.login({ username, password });
        const { token, user } = res.data;
        set({ authToken: token, authUser: user, creditBalance: user.credits || 0, configLoaded: false });
        localStorage.setItem('pmsupers_token', token);
      },

      register: async (username, email, password, displayName, inviteCode, verificationCode) => {
        const res = await authApi.register({ username, email, password, displayName, inviteCode: inviteCode || '', verificationCode: verificationCode || '' });
        const { token, user } = res.data;
        set({ authToken: token, authUser: user, creditBalance: user.credits || 0, configLoaded: false });
        localStorage.setItem('pmsupers_token', token);
      },

      wpLogin: async (wpToken, wpPassword, inviteCode?) => {
        try {
          const result = await authApi.wpLogin({ wp_token: wpToken, wp_password: wpPassword, invite_code: inviteCode });
          const data = result.data;
          if (data.code) {
            return {
              code: data.code,
              wpUser: data.wpUser,
              existingUserId: data.existingUserId,
              existingUsername: data.existingUsername,
            };
          }
          if (!data.token || !data.user) {
            throw new Error('登录失败，请重试');
          }
          set({ authToken: data.token, authUser: data.user, creditBalance: data.user.credits || 0, configLoaded: false });
          localStorage.setItem('pmsupers_token', data.token);
          return {};
        } catch (error: any) {
          if (error.response?.status === 400) {
            const errorData = error.response.data;
            if (errorData?.code) {
              return {
                code: errorData.code,
                wpUser: errorData.wpUser,
                existingUserId: errorData.existingUserId,
                existingUsername: errorData.existingUsername,
              };
            }
          }
          throw error;
        }
      },

  wpBindEmail: async (email, existingUserId, verificationCode, wpToken) => {
    const res = await authApi.wpBindEmail({ email, existing_user_id: existingUserId, verification_code: verificationCode, wp_token: wpToken });
    const { token, user } = res.data;
    set({ authToken: token, authUser: user, creditBalance: user.credits || 0, configLoaded: false });
    localStorage.setItem('pmsupers_token', token);
  },

      wpSendBindCode: async (wpToken) => {
        await authApi.wpSendBindCode({ wp_token: wpToken });
      },

      wpSetUsername: async (wpToken, wpPassword, newUsername, inviteCode?) => {
        const res = await authApi.wpSetUsername({ wp_token: wpToken, wp_password: wpPassword, new_username: newUsername, invite_code: inviteCode });
        const { token, user } = res.data;
        set({ authToken: token, authUser: user, creditBalance: user.credits || 0, configLoaded: false });
        localStorage.setItem('pmsupers_token', token);
      },

      wpRegister: async (wpToken, password, email) => {
        const res = await authApi.wpRegister({ wp_token: wpToken, password, email });
        const { token, user } = res.data;
        set({ authToken: token, authUser: user, creditBalance: user.credits || 0, configLoaded: false });
        localStorage.setItem('pmsupers_token', token);
      },

      setLocalPassword: async (password, currentPassword) => {
        await authApi.setLocalPassword({ password, currentPassword });
      },

      logout: () => {
        localStorage.removeItem('pmsupers_token');
        set({
          authToken: null,
          authUser: null,
          projects: [],
          sceneProjectMap: {},
          conversations: [],
          currentConversationId: null,
          chatMessages: [],
          configLoaded: false,
        });
        // 跳转到登录页面
        window.location.href = '/login';
      },

      setScenes: (scenes) => set({ scenes }),
      setTemplates: (templates) => set({ templates }),
      addCustomTemplate: (template) => set((s) => ({ customTemplates: [...s.customTemplates, template] })),
      removeCustomTemplate: (id) => set((s) => ({ customTemplates: s.customTemplates.filter((t) => t.id !== id) })),

      loadSceneProjects: async () => {
        try {
          const res = await projectsApi.list();
          const projects = res.data.projects || [];
          const sceneProjectMap: Record<string, { projectId: number; imageCount: number }> = {};
          for (const p of projects) {
            if (p.sceneId) {
              sceneProjectMap[p.sceneId] = { projectId: p.id, imageCount: p.imageCount || 0 };
            }
          }
          set({ projects, sceneProjectMap });
        } catch {
          set({ projects: [] });
        }
      },

      ensureSceneProject: async (sceneId) => {
        const { sceneProjectMap } = get();
        if (sceneProjectMap[sceneId]) return sceneProjectMap[sceneId].projectId;
        const sceneLabel = SCENE_LABELS[sceneId] || sceneId;
        const res = await projectsApi.create({ name: sceneLabel, sceneId });
        const id = res.data.id;
        set((s) => ({
          sceneProjectMap: { ...s.sceneProjectMap, [sceneId]: { projectId: id, imageCount: 0 } },
        }));
        return id;
      },

      fetchConversations: async () => {
        try {
          const res = await conversationsApi.list();
          set({ conversations: res.data.conversations || [] });
        } catch {
          set({ conversations: [] });
        }
      },

      createConversation: async (sceneId, phaseId, chartId, title) => {
        const res = await conversationsApi.create({ sceneId, phaseId, chartId, title });
        const conv = res.data;
        set((s) => ({
          conversations: [conv, ...s.conversations],
          currentConversationId: conv.id,
          chatMessages: [],
        }));
        return conv.id;
      },

      setCurrentConversation: (id) => set({ currentConversationId: id, chatMessages: [], currentConversationSelections: {} }),

      setCurrentConversationSelections: (selections) => set({ currentConversationSelections: selections }),

      loadConversation: async (id) => {
        try {
          const res = await conversationsApi.get(id);
          const conversation = res.data.conversation;
          set({
            currentConversationId: id,
            chatMessages: res.data.messages || [],
            currentConversationSelections: {
              phaseId: conversation?.phaseId,
              sceneId: conversation?.sceneId,
              chartId: conversation?.chartId,
            },
          });
        } catch {
          // silent fail
        }
      },

      deleteConversation: async (id) => {
        try {
          await conversationsApi.delete(id);
        } catch {
          // silent fail
        }
        set((s) => {
          const conversations = s.conversations.filter((c) => c.id !== id);
          const isCurrent = s.currentConversationId === id;
          return {
            conversations,
            currentConversationId: isCurrent ? null : s.currentConversationId,
            chatMessages: isCurrent ? [] : s.chatMessages,
          };
        });
      },

      renameConversation: async (id, title) => {
        try {
          await conversationsApi.update(id, { title });
        } catch {
          // silent fail
        }
        set((s) => ({
          conversations: s.conversations.map((c) => (c.id === id ? { ...c, title } : c)),
        }));
      },

      updateConversationTitle: (id, title) => {
        set((s) => ({
          conversations: s.conversations.map((c) => (c.id === id ? { ...c, title } : c)),
        }));
      },

      addChatMessage: (msg) => set((s) => ({ chatMessages: [...s.chatMessages, msg] })),

      updateMessageReaction: async (msgId, type) => {
        const { currentConversationId, chatMessages } = get();
        if (!currentConversationId) return;
        try {
          await conversationsApi.updateReaction(currentConversationId, msgId, type);
        } catch {
          // silent fail
        }
        set({
          chatMessages: chatMessages.map((m) =>
            m.id === msgId
              ? { ...m, isLiked: type === 'like', isDisliked: type === 'dislike' }
              : m
          ),
        });
      },

      updateMessageMetadata: (msgId, metadata) => {
        set((s) => ({
          chatMessages: s.chatMessages.map((m) =>
            m.id === msgId ? { ...m, metadata: { ...m.metadata, ...metadata } } : m
          ),
        }));
      },

      initChat: async () => {
        const { authToken, currentConversationId } = get();
        if (!authToken) return;
        await get().fetchConversations();
        if (currentConversationId) {
          await get().loadConversation(currentConversationId);
        }
      },

      setIsGenerating: (isGenerating) => set({ isGenerating }),
      setFilters: (filters) => set((s) => ({ filters: { ...s.filters, ...filters } })),
      setGenerateParams: (params) => set((s) => ({ generateParams: { ...s.generateParams, ...params } })),
      resetGenerateParams: () => set({ generateParams: defaultParams }),

      setUserProfile: (profile) => {
        set((s) => {
          const newProfile = { ...s.userProfile, ...profile };
          // 同步更新 authUser 中的 displayName 和 avatarUrl
          const authUserUpdates: Partial<AuthUser> = {};
          if (profile.displayName !== undefined) authUserUpdates.displayName = profile.displayName;
          if (profile.avatarUrl !== undefined) authUserUpdates.avatarUrl = profile.avatarUrl;
          const newAuthUser = Object.keys(authUserUpdates).length > 0
            ? { ...s.authUser, ...authUserUpdates } as AuthUser
            : s.authUser;
          debouncedSave(get);
          return { userProfile: newProfile, authUser: newAuthUser };
        });
      },

      setUserInitialized: (initialized) => set({ userInitialized: initialized }),

      loadConfigFromServer: async () => {
        const { authToken, configLoaded } = get();
        if (!authToken) return;
        if (configLoaded) return;
        try {
          console.log('Loading config from server...');
          const meRes = await authApi.me();
          console.log('Me response:', meRes.status);
          const [balRes, convRes] = await Promise.all([
            creditsApi.getBalance().catch(() => ({ data: { balance: 0 } })),
            conversationsApi.list().catch(() => ({ data: { conversations: [] } })),
          ]);
          const user = meRes.data;
          set({
            authUser: {
              id: user.id,
              username: user.username,
              email: user.email,
              displayName: user.displayName,
              avatarUrl: user.avatarUrl,
              createdAt: user.createdAt,
              industryId: user.industryId,
              industryRole: user.industryRole,
              city: user.city,
              workYears: user.workYears,
              bio: user.bio,
              identities: user.identities || ['regular'],
              isCoach: user.isCoach,
              isVip: user.isVip,
              isPermanentVip: user.isPermanentVip,
              vipEndTime: user.vipEndTime,
              userSource: user.userSource,
              wpUserId: user.wpUserId,
              credits: user.credits,
              totalRecharge: user.totalRecharge,
            },
            userProfile: {
              company: user.company || '',
              industry: user.industry || '',
              role: user.role || '',
              team: user.team || '',
              productStage: user.productStage || '',
              industryId: user.industryId,
              industryRole: user.industryRole,
              city: user.city,
              workYears: user.workYears,
              bio: user.bio,
            },
            userInitialized: !!user.initialized,
            configLoaded: true,
            creditBalance: balRes.data.balance,
            conversations: convRes.data.conversations || [],
          });
        } catch (err: any) {
          console.error('loadConfigFromServer error:', err);
          // 用户被删除、被禁用或认证失效时退出登录
          // 尝试多种方式获取状态码
          const status = err.response?.status || err.status || (err.message?.includes('401') ? 401 : null);
          console.log('Error status:', status, 'Error message:', err.message);
          if (status === 401 || status === 403 || status === 404) {
            console.log('Logging out due to error status:', status);
            get().logout();
          } else {
            // 对于其他错误，仍然标记为已加载以避免无限重试
            // 但保留现有的 authUser 数据
            console.log('Non-auth error occurred, marking config as loaded to prevent retry loop');
            set({ configLoaded: true });
          }
        }
      },

      saveConfigToServer: async () => {
        const { authToken, userProfile } = get();
        if (!authToken) return;
        try {
          await configApi.updateUserConfig({ profile: userProfile });
        } catch {
          // silent fail
        }
      },

      fetchCreditBalance: async () => {
        try {
          const res = await creditsApi.getBalance();
          set({ creditBalance: res.data.balance });
        } catch {
          // silent fail
        }
      },

      fetchAuthUser: async () => {
        try {
          const res = await authApi.me();
          const user = res.data;
          set({
            authUser: {
              ...get().authUser,
              id: user.id,
              username: user.username,
              email: user.email,
              displayName: user.displayName,
              avatarUrl: user.avatarUrl,
              credits: user.credits,
              totalRecharge: user.totalRecharge,
              inviteCode: user.inviteCode,
              isCoach: user.isCoach,
              isCoachVerified: user.isCoachVerified,
            },
            creditBalance: user.credits,
          });
        } catch {
          // silent fail
        }
      },
    }),
    {
      name: 'pmsupers-store',
      partialize: (state) => ({
        authToken: state.authToken,
        authUser: state.authUser,
        currentConversationId: state.currentConversationId,
        userProfile: state.userProfile,
        userInitialized: state.userInitialized,
        generateParams: state.generateParams,
        customTemplates: state.customTemplates,
      }),
    }
  )
);